import { ApolloError } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import React from 'react';
import {
  DataGrid,
  createTableColumn,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Divider,
  FluentProvider,
  makeStyles,
  Skeleton,
  SkeletonItem,
  TableColumnSizingOptions,
  webLightTheme,
} from '@fluentui/react-components';
import useToast from '../../../utils/hooks/useToast';
import ICompanyMilestoneDocument from '../../../utils/types/ICompanyMilestoneDocument';
import { Toast } from '../../notifications/StatNotification';
import DetailListPaginationBanner from '../DetailsLists/pagination/DetailsListPaginationBanner';
import IPaginationMetadata from '../DetailsLists/pagination/IPaginationMetadata';
import getDocumentListColumns from './DocumentList.config';

const useStyles = makeStyles({
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 0',
  },
  cell: {
    padding: '0 8px',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    '& > *': {
      minWidth: 0,
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  title: {
    marginBottom: 0,
    fontSize: '1em',
  },
});

const columnSizingOptions: TableColumnSizingOptions = {
  file: { minWidth: 16, defaultWidth: 40, idealWidth: 40 },
  title: { minWidth: 210, defaultWidth: 300, idealWidth: 350 },
  dateUploadedValue: { minWidth: 100, defaultWidth: 140, idealWidth: 150 },
  uploadedBy: { minWidth: 70, defaultWidth: 120, idealWidth: 130 },
  processType: { minWidth: 90, defaultWidth: 110, idealWidth: 120 },
  milestoneName: { minWidth: 100, defaultWidth: 150, idealWidth: 160 },
  processName: { minWidth: 150, defaultWidth: 350, idealWidth: 400 },
  actions: { minWidth: 140, defaultWidth: 160, idealWidth: 160 },
};

interface IDocumentsListProps {
  docType: string;
  documents: ICompanyMilestoneDocument[];
  loading: boolean;
  showDeletedFiles: boolean;
  error?: ApolloError;
  paginationData: IPaginationMetadata;
  renderDocumentAction?: (document: ICompanyMilestoneDocument) => JSX.Element;
}

const DocumentsList = ({
  docType,
  documents,
  loading,
  showDeletedFiles,
  error,
  paginationData,
  renderDocumentAction,
}: IDocumentsListProps): JSX.Element => {
  const styles = useStyles();

  if (error) {
    return <></>;
  }

  const { createToast } = useToast();

  const onDocumentError = (message?: string): void => {
    const toast: Toast = {
      id: Math.random(),
      content: message || 'Unable to download the selected document.',
      header: 'Document Download Error',
      iconName: 'ErrorBadge',
    };
    createToast(toast);
  };

  const title = showDeletedFiles ? 'Recycled' : docType;
  const itemColumns = getDocumentListColumns(onDocumentError);
  if (renderDocumentAction) {
    itemColumns.push(
      createTableColumn<ICompanyMilestoneDocument>({
        columnId: 'actions',
        renderHeaderCell: () => 'Text Extraction',
        renderCell: renderDocumentAction,
      }),
    );
  }

  return (
    <Stack>
      <div>
        <h2 className={styles.title}>{`${title} Documents`}</h2>
        <Divider />
      </div>

      {loading && (
        <Skeleton aria-label="Loading documents" className={styles.skeleton}>
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
        </Skeleton>
      )}

      {!loading && (
        <>
          {documents.length === 0 ? (
            <p>No documents found. Try changing the filters or uploading documents.</p>
          ) : (
            <>
              <FluentProvider theme={webLightTheme}>
                <DataGrid
                  items={documents}
                  columns={itemColumns}
                  resizableColumns
                  columnSizingOptions={columnSizingOptions}
                  aria-label={`${title} Documents details`}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<ICompanyMilestoneDocument>>
                    {({ item, rowId }) => (
                      <DataGridRow<ICompanyMilestoneDocument> key={rowId}>
                        {({ renderCell }) => (
                          <DataGridCell className={styles.cell}>{renderCell(item)}</DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </FluentProvider>
              <DetailListPaginationBanner {...paginationData} />
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export default DocumentsList;
